import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { FiChevronLeft, FiChevronRight, FiRepeat, FiSearch, FiX } from "react-icons/fi";
import type { InventoryPool } from "../../services/inventoryPools";

type TransferInventoryItem = {
  _id: string;
  itemName: string;
  quantity: number;
  sku?: string;
  category?: string | { _id?: string; name?: string; scope?: string };
  categoryName?: string;
  categoryId?: string | { _id?: string; name?: string; scope?: string };
  businessName?: string;
  imageUrl?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  pools: InventoryPool[];
  sourcePoolId: string;
  items: TransferInventoryItem[];
  onLoadItems: (poolId: string) => Promise<TransferInventoryItem[]>;
  onTransfer: (payload: {
    fromPoolId: string;
    toPoolId: string;
    items: Array<{ inventoryItemId: string; quantity: number }>;
  }) => Promise<void>;
};

export default function PoolTransferModalWeb({
  open,
  onClose,
  pools,
  sourcePoolId,
  items,
  onLoadItems,
  onTransfer,
}: Props) {
  const categoryScrollRef = useRef<HTMLDivElement>(null);
  const [fromPoolId, setFromPoolId] = useState("");
  const [toPoolId, setToPoolId] = useState("");
  const [sourceItems, setSourceItems] = useState<TransferInventoryItem[]>([]);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sourceLoading, setSourceLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const fallback =
      pools.find((pool) => String(pool._id) !== String(sourcePoolId))?._id || "";
    setFromPoolId(String(sourcePoolId));
    setToPoolId(String(fallback));
    setSourceItems(items);
    setQuantities({});
    setSearchTerm("");
    setSelectedCategory("All");
  }, [open, pools, sourcePoolId]);

  useEffect(() => {
    if (!open || !fromPoolId) return;
    let mounted = true;
    setQuantities({});
    setSearchTerm("");
    setSelectedCategory("All");

    if (String(fromPoolId) === String(sourcePoolId)) {
      setSourceItems(items);
      setSourceLoading(false);
      return;
    }

    (async () => {
      try {
        setSourceLoading(true);
        const nextItems = await onLoadItems(fromPoolId);
        if (mounted) setSourceItems(Array.isArray(nextItems) ? nextItems : []);
      } catch (error: any) {
        console.error("Failed to load transfer source pool:", error?.message || error);
        if (mounted) setSourceItems([]);
      } finally {
        if (mounted) setSourceLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [fromPoolId, items, onLoadItems, open, sourcePoolId]);

  const totalUnits = useMemo(
    () =>
      Object.values(quantities).reduce(
        (sum, qty) => sum + Math.max(0, Number(qty) || 0),
        0
      ),
    [quantities]
  );

  const getCategoryLabel = (item: TransferInventoryItem) => {
    const categoryIdName =
      item.categoryId && typeof item.categoryId === "object"
        ? item.categoryId.name
        : undefined;
    const categoryObjectName =
      item.category && typeof item.category === "object"
        ? item.category.name
        : undefined;
    const raw =
      item.categoryName ?? categoryIdName ?? categoryObjectName ?? item.category;
    return (typeof raw === "string" ? raw.trim() : "") || "Uncategorized";
  };

  const filteredItems = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return sourceItems.filter((item) => {
      const category = getCategoryLabel(item);
      const matchesCategory =
        selectedCategory === "All" || category === selectedCategory;
      const matchesSearch =
        !normalizedSearch ||
        [item.itemName, item.sku, category, item.businessName]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(normalizedSearch)
        );
      return matchesCategory && matchesSearch;
    });
  }, [sourceItems, searchTerm, selectedCategory]);

  const categories = useMemo(() => {
    const uniqueCategories = Array.from(
      new Set(sourceItems.map(getCategoryLabel).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));
    return ["All", ...uniqueCategories];
  }, [sourceItems]);

  const isAllSelected = useMemo(() => {
    if (!filteredItems.length) return false;
    return filteredItems.every(
      (item) => (quantities[item._id] || 0) >= item.quantity
    );
  }, [filteredItems, quantities]);

  const canTransfer =
    !saving &&
    !sourceLoading &&
    !!fromPoolId &&
    !!toPoolId &&
    fromPoolId !== toPoolId &&
    totalUnits > 0;

  const handleTransfer = async () => {
    if (!canTransfer) return;
    setSaving(true);
    try {
      await onTransfer({
        fromPoolId,
        toPoolId,
        items: Object.entries(quantities)
          .filter(([, quantity]) => quantity > 0)
          .map(([inventoryItemId, quantity]) => ({
            inventoryItemId,
            quantity,
          })),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const scrollCategoriesBy = (distance: number) => {
    categoryScrollRef.current?.scrollBy({ left: distance, behavior: "smooth" });
  };

  const handleCategoryWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (!categoryScrollRef.current) return;
    e.preventDefault();
    categoryScrollRef.current.scrollLeft += (e.deltaY || e.deltaX) * 2;
  };

  const handleCategoryDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!categoryScrollRef.current) return;
    const container = categoryScrollRef.current;
    const startX = e.clientX;
    const startScrollLeft = container.scrollLeft;

    const onMouseMove = (event: MouseEvent) => {
      container.scrollLeft = startScrollLeft - (event.clientX - startX) * 2;
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  if (!open) return null;

  return (
    <Backdrop onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Title>Transfer Between Pools</Title>
        <Row>
          <FieldGroup>
            <Label>From Pool</Label>
            <Select
              value={fromPoolId}
              onChange={(e) => {
                const nextFromPoolId = e.target.value;
                setFromPoolId(nextFromPoolId);
                if (nextFromPoolId === toPoolId) {
                  setToPoolId(
                    String(
                      pools.find(
                        (pool) => String(pool._id) !== String(nextFromPoolId)
                      )?._id || ""
                    )
                  );
                }
              }}
              disabled={saving || sourceLoading}
            >
              {pools
                .filter((pool) => String(pool._id) !== String(toPoolId))
                .map((pool) => (
                  <option key={pool._id} value={pool._id}>
                    {pool.name}
                  </option>
                ))}
            </Select>
          </FieldGroup>
          <SwapButton
            type="button"
            onClick={() => {
              setFromPoolId(toPoolId);
              setToPoolId(fromPoolId);
            }}
            disabled={saving || sourceLoading || !fromPoolId || !toPoolId}
            aria-label="Swap transfer pools"
            title="Swap transfer pools"
          >
            <FiRepeat size={18} />
          </SwapButton>
          <FieldGroup>
            <Label>To Pool</Label>
            <Select
              value={toPoolId}
              onChange={(e) => setToPoolId(e.target.value)}
              disabled={saving}
            >
              <option value="">Select destination</option>
              {pools
                .filter((pool) => String(pool._id) !== String(fromPoolId))
                .map((pool) => (
                  <option key={pool._id} value={pool._id}>
                    {pool.name}
                  </option>
                ))}
            </Select>
          </FieldGroup>
        </Row>

        <MetaRow>
          <Meta>Selected units: {totalUnits}</Meta>
          <SelectAllButton
            type="button"
            disabled={!filteredItems.length || saving || sourceLoading}
            onClick={() => {
              if (isAllSelected) {
                setQuantities((prev) => {
                  const next = { ...prev };
                  filteredItems.forEach((item) => {
                    delete next[item._id];
                  });
                  return next;
                });
                return;
              }
              setQuantities((prev) =>
                filteredItems.reduce<Record<string, number>>((acc, item) => {
                  acc[item._id] = item.quantity;
                  return acc;
                }, { ...prev })
              );
            }}
          >
            {isAllSelected ? "Unselect All" : "Select All"}
          </SelectAllButton>
        </MetaRow>

        <SearchWrap>
          {searchTerm ? (
            <ClearSearchButton
              type="button"
              onClick={() => setSearchTerm("")}
              aria-label="Clear transfer item search"
              disabled={saving}
            >
              <FiX size={18} />
            </ClearSearchButton>
          ) : (
            <SearchIcon size={18} aria-hidden="true" />
          )}
          <SearchInput
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search transferable items..."
            disabled={saving || sourceLoading}
          />
        </SearchWrap>

        <CategoryControlRow>
          <CategoryArrowButton
            type="button"
            left
            onClick={() => scrollCategoriesBy(-150)}
            aria-label="Scroll categories left"
          >
            <FiChevronLeft />
          </CategoryArrowButton>
          <CategoryChips
            ref={categoryScrollRef}
            aria-label="Transfer item categories"
            onWheel={handleCategoryWheel}
            onMouseDown={handleCategoryDrag}
          >
            {categories.map((category) => (
              <CategoryChip
                key={category}
                type="button"
                $active={selectedCategory === category}
                onClick={() => setSelectedCategory(category)}
                disabled={saving || sourceLoading}
              >
                {category}
              </CategoryChip>
            ))}
          </CategoryChips>
          <CategoryArrowButton
            type="button"
            onClick={() => scrollCategoriesBy(150)}
            aria-label="Scroll categories right"
          >
            <FiChevronRight />
          </CategoryArrowButton>
        </CategoryControlRow>

        <List>
          {sourceLoading ? (
            <Empty>Loading transferable items...</Empty>
          ) : filteredItems.length ? (
            filteredItems.map((item) => {
              const value = quantities[item._id] || 0;
              const remainingAvailable = Math.max(0, item.quantity - value);
              const categoryLabel = getCategoryLabel(item);
              const canAddItem = !saving && value < item.quantity;
              const handleQuantityChange = (rawValue: string) => {
                const digitsOnly = rawValue.replace(/[^\d]/g, "");
                const nextValue = digitsOnly ? Number(digitsOnly) : 0;
                setQuantities((prev) => ({
                  ...prev,
                  [item._id]: Math.max(0, Math.min(item.quantity, nextValue)),
                }));
              };
              const addItem = () => {
                if (!canAddItem) return;
                setQuantities((prev) => ({
                  ...prev,
                  [item._id]: Math.min(item.quantity, (prev[item._id] || 0) + 1),
                }));
              };
              return (
                <ItemCard
                  key={item._id}
                  role="button"
                  tabIndex={canAddItem ? 0 : -1}
                  $canAdd={canAddItem}
                  onClick={addItem}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      addItem();
                    }
                  }}
                >
                  <ItemDetails>
                    <ItemImageWrap>
                      {item.imageUrl ? (
                        <ItemImage src={item.imageUrl} alt={item.itemName} />
                      ) : (
                        <ItemImageFallback>No Image</ItemImageFallback>
                      )}
                    </ItemImageWrap>
                    <ItemInfo>
                      <ItemName>{item.itemName}</ItemName>
                      <ItemCategory>{categoryLabel}</ItemCategory>
                      <ItemMeta>Available: {remainingAvailable}</ItemMeta>
                    </ItemInfo>
                  </ItemDetails>
                  <QtyControls onClick={(e) => e.stopPropagation()}>
                    <QtyButton
                      type="button"
                      onClick={() =>
                        setQuantities((prev) => ({
                          ...prev,
                          [item._id]: Math.max(0, (prev[item._id] || 0) - 1),
                        }))
                      }
                      disabled={saving || value <= 0}
                      aria-label={`Remove one ${item.itemName}`}
                    >
                      -
                    </QtyButton>
                    <QtyInput
                      type="text"
                      inputMode="numeric"
                      value={String(value)}
                      onChange={(e) => handleQuantityChange(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      disabled={saving}
                      aria-label={`Transfer quantity for ${item.itemName}`}
                    />
                    <QtyButton
                      type="button"
                      onClick={() =>
                        setQuantities((prev) => ({
                          ...prev,
                          [item._id]: Math.min(
                            item.quantity,
                            (prev[item._id] || 0) + 1
                          ),
                        }))
                      }
                      disabled={saving || value >= item.quantity}
                      aria-label={`Add one ${item.itemName}`}
                    >
                      +
                    </QtyButton>
                  </QtyControls>
                </ItemCard>
              );
            })
          ) : (
            <Empty>
              {sourceItems.length
                ? "No transferable items match your search."
                : "No transferable items in this pool."}
            </Empty>
          )}
        </List>

        <Actions>
          <SecondaryButton type="button" onClick={onClose} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="button"
            disabled={!canTransfer}
            onClick={handleTransfer}
          >
            {saving ? "Transferring..." : "Transfer"}
          </PrimaryButton>
        </Actions>
      </Modal>
    </Backdrop>
  );
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1rem;
`;

const Modal = styled.div`
  width: min(760px, 100%);
  max-height: 85vh;
  overflow-x: hidden;
  overflow-y: auto;
  background: #fff;
  border-radius: 20px;
  padding: 1.5rem 1.5rem 0;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.22);
  scrollbar-gutter: stable;
`;

const Title = styled.h3`
  margin: 0 0 1rem;
  font-size: 1.35rem;
  font-weight: 700;
  color: #0f172a;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
  align-items: end;

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr) auto;

    & > div:last-child {
      grid-column: 1 / -1;
    }
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const Label = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
`;

const Select = styled.select`
  width: 100%;
  min-height: 44px;
  padding: 0.75rem 0.9rem;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  color: #0f172a;
  background: #fff;
`;

const SwapButton = styled.button`
  width: 44px;
  height: 44px;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  background: #fff;
  color: #334155;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #2563eb;
    color: #2563eb;
    background: #eff6ff;
  }

  &:disabled {
    background: #f8fafc;
    color: #94a3b8;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    margin-top: 1.55rem;
  }
`;

const Meta = styled.div`
  color: #475569;
  font-size: 0.95rem;
  font-weight: 600;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.85rem;

  @media (max-width: 768px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const SelectAllButton = styled.button`
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    color: #94a3b8;
    cursor: not-allowed;
  }
`;

const SearchWrap = styled.div`
  position: relative;
  margin-bottom: 0.85rem;
`;

const SearchInput = styled.input`
  width: 100%;
  min-height: 44px;
  padding: 0.75rem 0.9rem 0.75rem 2.45rem;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  color: #0f172a;
  background: #fff;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.14);
  }

  &:disabled {
    background: #f8fafc;
    color: #94a3b8;
  }
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
`;

const ClearSearchButton = styled.button`
  position: absolute;
  left: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 9999px;
  background: transparent;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #e2e8f0;
    color: #0f172a;
  }

  &:disabled {
    cursor: not-allowed;
    color: #94a3b8;
  }
`;

const CategoryControlRow = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.85rem;
  min-width: 0;
`;

const CategoryArrowButton = styled.button<{ left?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.92);
  color: #475569;
  cursor: pointer;
  box-shadow: 0 1px 6px rgba(15, 23, 42, 0.12);

  &:hover {
    background: #fff;
    color: #0f172a;
  }

  @media (max-width: 768px) {
    width: 26px;
    height: 26px;
  }
`;

const CategoryChips = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 0.5rem;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 0.15rem;
  cursor: grab;
  user-select: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &:active {
    cursor: grabbing;
  }
`;

const CategoryChip = styled.button<{ $active?: boolean }>`
  border: 1px solid ${({ $active }) => ($active ? "#2563eb" : "#cbd5e1")};
  background: ${({ $active }) => ($active ? "#eff6ff" : "#fff")};
  color: ${({ $active }) => ($active ? "#1d4ed8" : "#475569")};
  border-radius: 9999px;
  padding: 0.42rem 0.75rem;
  font-size: 0.82rem;
  font-weight: 700;
  flex: 0 0 auto;
  cursor: pointer;

  &:disabled {
    color: #94a3b8;
    cursor: not-allowed;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-bottom: 0.25rem;
`;

const ItemCard = styled.div<{ $canAdd?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 0.9rem 1rem;
  background: #f8fafc;
  cursor: ${({ $canAdd }) => ($canAdd ? "pointer" : "default")};

  &:hover {
    border-color: ${({ $canAdd }) => ($canAdd ? "#bfdbfe" : "#e2e8f0")};
    background: ${({ $canAdd }) => ($canAdd ? "#eff6ff" : "#f8fafc")};
  }

  &:focus-visible {
    outline: 3px solid rgba(37, 99, 235, 0.18);
    border-color: #2563eb;
  }
`;

const ItemDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-width: 0;
`;

const ItemImageWrap = styled.div`
  width: 56px;
  height: 56px;
  flex: 0 0 56px;
  border-radius: 10px;
  overflow: hidden;
  background: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ItemImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ItemImageFallback = styled.span`
  padding: 0 0.3rem;
  text-align: center;
  color: #64748b;
  font-size: 0.65rem;
  font-weight: 700;
  line-height: 1.1;
`;

const ItemInfo = styled.div`
  min-width: 0;
`;

const ItemName = styled.div`
  font-weight: 700;
  color: #0f172a;
`;

const ItemCategory = styled.div`
  color: #334155;
  font-size: 0.82rem;
  font-weight: 600;
  margin-top: 0.15rem;
`;

const ItemMeta = styled.div`
  color: #64748b;
  font-size: 0.9rem;
  margin-top: 0.2rem;
`;

const QtyControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const QtyButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  border: none;
  background: #2563eb;
  color: #fff;
  font-size: 1rem;
  cursor: pointer;

  &:disabled {
    background: #cbd5e1;
    cursor: not-allowed;
  }
`;

const QtyInput = styled.input`
  width: 64px;
  min-width: 64px;
  height: 36px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 0 0.45rem;
  text-align: center;
  font-weight: 700;
  color: #0f172a;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.14);
  }

  &:disabled {
    background: #f8fafc;
    color: #94a3b8;
  }
`;

const Empty = styled.div`
  padding: 1.5rem 0.5rem;
  text-align: center;
  color: #64748b;
`;

const Actions = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.25rem;
  margin-left: -1.5rem;
  margin-right: -1.5rem;
  padding: 0.95rem 1.5rem 1.1rem;
  border-top: 1px solid #e2e8f0;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 -10px 24px rgba(15, 23, 42, 0.08);
  border-bottom-right-radius: 20px;
  z-index: 2;

  @media (max-width: 768px) {
    justify-content: stretch;

    button {
      flex: 1;
    }
  }
`;

const SecondaryButton = styled.button`
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #334155;
  border-radius: 12px;
  padding: 0.75rem 1rem;
  cursor: pointer;
`;

const PrimaryButton = styled.button`
  border: 1px solid #2563eb;
  background: #2563eb;
  color: #fff;
  border-radius: 12px;
  padding: 0.75rem 1rem;
  cursor: pointer;

  &:disabled {
    border-color: #cbd5e1;
    background: #cbd5e1;
    cursor: not-allowed;
  }
`;
